import { Block, IconButton } from '@aloft/toolkit-sui';

import { PropsWithChildren, ReactNode } from 'react';

import { HelpDrawer } from '../../components/ReferenceBar/HelpDrawer/HelpDrawer.tsx';
import { ResizableDrawerLayout } from '../../layout/ResizableDrawerLayout.tsx';
import { setReferenceViewOpen } from '../../redux/features/referenceBarUISlice.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { selectDeviceWidth } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { Tkv2ReferenceViewModule } from './Tkv2ReferenceViewModule.tsx';
import { useAutoOpenHelpDrawer } from './hooks/useAutoOpenHelpDrawer.ts';
import { useHelpDrawerPageID } from './hooks/useHelpDrawerPageID.ts';
import { resolveTkv2ReferenceDrawerMode } from './resolveTkv2ReferenceDrawerMode.ts';

import './Tkv2ReferenceView.scss';

type Tkv2ReferenceViewDrawerProps = PropsWithChildren<{
  drawerContent?: ReactNode;
  canClose?: boolean;
}>;

export const Tkv2ReferenceViewDrawer = ({
  children,
  drawerContent,
  canClose = true,
}: Tkv2ReferenceViewDrawerProps) => {
  const deviceWidth = useAppSelector(selectDeviceWidth);
  const { referenceViewOpen, selectedDataSheet } = useAppSelector(selectReferenceBarUI);
  const dispatch = useAppDispatch();

  const { pageID } = useHelpDrawerPageID();
  useAutoOpenHelpDrawer({ pageID });

  const drawerMode = resolveTkv2ReferenceDrawerMode({
    selectedDataSheet,
    drawerContent,
  });

  const resolvedDrawerContent =
    drawerMode === 'help' ? (
      <HelpDrawer pageID={pageID} />
    ) : drawerMode === 'custom' ? (
      drawerContent
    ) : (
      <Tkv2ReferenceViewModule isPopout={false} dataSheet={selectedDataSheet} />
    );

  return (
    <Block className="reference-view-drawer">
      <div className="reference-view-drawer-desktop">
        <ResizableDrawerLayout
          direction="horizontal"
          isOpen={referenceViewOpen}
          onClose={() => dispatch(setReferenceViewOpen(false))}
          drawerContent={resolvedDrawerContent}
          isResizable
          defaultSize={33}
          canClose={canClose}
        >
          {children}
        </ResizableDrawerLayout>
      </div>
      {deviceWidth === 'phone' && (
        <div className="reference-view-drawer-mobile">
          {referenceViewOpen ? (
            <div>
              {canClose && (
                <div className="reference-view-close-button">
                  <IconButton
                    icon="close-circle"
                    variant="tertiary"
                    onClick={() => dispatch(setReferenceViewOpen(false))}
                    ariaLabel="close"
                    iconSize="lg"
                    size="sm"
                    iconColor="textTertiary"
                  />
                </div>
              )}
              {resolvedDrawerContent}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </Block>
  );
};
